import React, { useState, useEffect } from 'react';
import { ref, get, push, set, remove, update } from 'firebase/database';
import { realtimeDb } from '../firebase';
import './AdminDashboard.css';

interface StudentData {
  name: string;
  englishName: string;
  class?: string;
  code?: string;
}

interface TeacherData {
  name: string;
  code?: string;
}

interface ClassData {
  name: string;
  teacher?: string;
}

interface AdminDashboardProps {
}

type TabType = 'classes' | 'students' | 'teachers' | 'classesTest';

// TeacherGroup 컴포넌트
interface TeacherGroupProps {
  teacher: any;
  onEditTeacher: () => void;
  onDeleteTeacher: () => void;
}

// ClassGroup 컴포넌트
interface ClassGroupProps {
  classItem: any;
  onEditClass: () => void;
  onDeleteClass: () => void;
  onEditStudent: (student: any) => void;
  onDeleteStudent: (student: any) => void;
}

const TeacherGroup: React.FC<TeacherGroupProps> = ({ 
  teacher, 
  onEditTeacher, 
  onDeleteTeacher 
}) => {
  const [teacherClasses, setTeacherClasses] = useState<any[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  useEffect(() => {
    if (teacher.code) {
      loadTeacherClasses();
    }
  }, [teacher.code]);

  const loadTeacherClasses = async () => {
    setLoadingClasses(true);
    try {
      const teacherClassesRef = ref(realtimeDb, `teachers/code/${teacher.code}/classes`);
      const snapshot = await get(teacherClassesRef);
      
      if (snapshot.exists()) {
        const classesData = snapshot.val();
        const classesArray = Object.entries(classesData).map(([name, data]: [string, any]) => ({
          name,
          ...data
        }));
        
        // 각 반의 학생 수 로드
        const classesWithStudentCount = await Promise.all(
          classesArray.map(async (classItem) => {
            try {
              const classStudentsRef = ref(realtimeDb, `classes/${classItem.name}/students`);
              const studentsSnapshot = await get(classStudentsRef);
              const studentCount = studentsSnapshot.exists() ? Object.keys(studentsSnapshot.val()).length : 0;
              return {
                ...classItem,
                studentCount
              };
            } catch (error) {
              console.error(`반 ${classItem.name}의 학생 수 로드 실패:`, error);
              return {
                ...classItem,
                studentCount: 0
              };
            }
          })
        );
        
        setTeacherClasses(classesWithStudentCount);
      } else {
        setTeacherClasses([]);
      }
    } catch (error) {
      console.error('선생님 담당반 목록 불러오기 실패:', error);
      setTeacherClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  return (
    <div className="teacher-group">
      <div className="teacher-group-header">
        <div className="teacher-group-title-row">
          <div className="teacher-group-info">
            <h4 className="teacher-group-title">
              {teacher.name}
              <span className="class-count">({teacherClasses.length}개 반)</span>
            </h4>
            <p className="teacher-code">
              선생님 코드: <span className="code-value">{teacher.code}</span>
            </p>
          </div>
          <div className="teacher-group-actions">
            <button 
              onClick={onEditTeacher}
              className="edit-button-small"
              title="선생님 정보 수정"
            >
              수정
            </button>
            <button 
              onClick={onDeleteTeacher}
              className="delete-button-small"
              title="선생님 삭제"
            >
              삭제
            </button>
          </div>
        </div>
      </div>
      <div className="classes-table-container">
        {loadingClasses ? (
          <div className="loading">담당반 목록을 불러오는 중...</div>
        ) : teacherClasses.length > 0 ? (
          <table className="classes-table">
            <thead>
              <tr>
                <th>반 이름</th>
                <th>인원수</th>
                <th>추가일</th>
              </tr>
            </thead>
            <tbody>
              {teacherClasses
                .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                .map((classItem) => (
                  <tr key={classItem.name} className="class-row">
                    <td>{classItem.name}</td>
                    <td>{classItem.studentCount || 0}명</td>
                    <td>{new Date(classItem.addedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <div className="no-classes">
            <p>이 선생님이 담당하는 반이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ClassGroup: React.FC<ClassGroupProps> = ({ 
  classItem, 
  onEditClass, 
  onDeleteClass, 
  onEditStudent, 
  onDeleteStudent 
}) => {
  const [classStudents, setClassStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (classItem.name) {
      loadClassStudents();
    }
  }, [classItem.name]);

  const loadClassStudents = async () => {
    setLoadingStudents(true);
    try {
      const classStudentsRef = ref(realtimeDb, `classes/${classItem.name}/students`);
      const snapshot = await get(classStudentsRef);
      
      if (snapshot.exists()) {
        const studentsData = snapshot.val();
        const studentsArray = Object.entries(studentsData).map(([code, data]: [string, any]) => ({
          code,
          ...data
        }));
        setClassStudents(studentsArray);
      } else {
        setClassStudents([]);
      }
    } catch (error) {
      console.error('반 학생 목록 불러오기 실패:', error);
      setClassStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  return (
    <div className="class-group">
      <div className="class-group-header">
        <div className="class-group-title-row">
          <div className="class-group-info">
            <h4 className="class-group-title">
              {classItem.name}
              <span className="student-count">({classStudents.length}명)</span>
            </h4>
            {classItem.teacherName ? (
              <p className="class-teacher">
                담당 선생님: <span className="teacher-name">{classItem.teacherName}</span>
              </p>
            ) : (
              <p className="class-teacher no-teacher">
                담당 선생님: <span className="teacher-name">미지정</span>
              </p>
            )}
          </div>
          <div className="class-group-actions">
            <button 
              onClick={onEditClass}
              className="edit-button-small"
              title="반 정보 수정"
            >
              수정
            </button>
            <button 
              onClick={onDeleteClass}
              className="delete-button-small"
              title="반 삭제"
            >
              삭제
            </button>
          </div>
        </div>
      </div>
      <div className="students-table-container">
        {loadingStudents ? (
          <div className="loading">학생 목록을 불러오는 중...</div>
        ) : classStudents.length > 0 ? (
          <table className="students-table">
            <thead>
              <tr>
                <th>이름</th>
                <th>영어 이름</th>
                <th>코드</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {classStudents
                .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                .map((student) => (
                  <tr key={student.code} className="student-row">
                    <td>{student.name}</td>
                    <td>{student.englishName}</td>
                    <td>{student.code}</td>
                    <td>
                      <button 
                        onClick={() => onEditStudent(student)}
                        className="edit-button"
                        title="학생 정보 수정"
                      >
                        수정
                      </button>
                      <button 
                        onClick={() => onDeleteStudent({ ...student, class: classItem.name })}
                        className="delete-button-small"
                        title="학생 삭제"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <div className="no-students">
            <p>이 반에 소속된 학생이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const [activeTab, setActiveTab] = useState<TabType>('students');
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [showEditTeacherModal, setShowEditTeacherModal] = useState(false);
  const [showDeleteStudentModal, setShowDeleteStudentModal] = useState(false);
  const [showDeleteTeacherModal, setShowDeleteTeacherModal] = useState(false);
  const [showTeacherManageModal, setShowTeacherManageModal] = useState(false);
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showEditClassModal, setShowEditClassModal] = useState(false);
  const [showDeleteClassModal, setShowDeleteClassModal] = useState(false);
     const [showStudentListModal, setShowStudentListModal] = useState(false);
   const [selectedClassStudents, setSelectedClassStudents] = useState<any[]>([]);
   const [selectedClassName, setSelectedClassName] = useState('');
   const [showTeacherClassesModal, setShowTeacherClassesModal] = useState(false);
   const [selectedTeacherClasses, setSelectedTeacherClasses] = useState<any[]>([]);
   const [selectedTeacherName, setSelectedTeacherName] = useState('');
  const [students, setStudents] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
     // 학생 추가 폼 상태
   const [studentForm, setStudentForm] = useState<StudentData>({
     name: '',
     englishName: '',
     class: '',
     code: ''
   });

     // 학생 수정 폼 상태
   const [editStudentForm, setEditStudentForm] = useState<StudentData>({
     name: '',
     englishName: '',
     class: '',
     code: ''
   });
  
  // 선생님 추가 폼 상태
  const [teacherForm, setTeacherForm] = useState<TeacherData>({
    name: '',
    code: ''
  });

  // 반 추가 폼 상태
  const [classForm, setClassForm] = useState<ClassData>({
    name: '',
    teacher: ''
  });

  // 삭제 폼 상태
  const [deleteForm, setDeleteForm] = useState({
    code: ''
  });

  // 선생님 관리 학생 추가 폼 상태
  const [manageStudentForm, setManageStudentForm] = useState({
    code: ''
  });

  // 삭제할 항목 정보
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  // 관리할 선생님 정보
  const [selectedTeacher, setSelectedTeacher] = useState<any>(null);

  // 선생님이 관리하는 학생 목록
  const [managedStudents, setManagedStudents] = useState<any[]>([]);
  
  // 반 수정 시 원본 반 이름 저장
  const [originalClassName, setOriginalClassName] = useState<string>('');
  
  // 반 필터링 상태
  const [selectedClassFilter, setSelectedClassFilter] = useState<string>('all');
  
  // 선생님 필터링 상태
  const [selectedTeacherFilter, setSelectedTeacherFilter] = useState<string>('all');
  
  // 반 관리 탭용 선생님 필터링 상태
  const [selectedTeacherFilterForClasses, setSelectedTeacherFilterForClasses] = useState<string>('all');
  
  // 반관리Test 탭용 선생님 필터링 상태
  const [selectedTeacherFilterForTest, setSelectedTeacherFilterForTest] = useState<string>('all');
  
  // 학생 검색 상태
  const [studentSearchTerm, setStudentSearchTerm] = useState<string>('');
  
  const [formErrors, setFormErrors] = useState<Partial<StudentData>>({});
  const [editFormErrors, setEditFormErrors] = useState<Partial<StudentData>>({});
  const [teacherFormErrors, setTeacherFormErrors] = useState<Partial<TeacherData>>({});
  const [classFormErrors, setClassFormErrors] = useState<Partial<ClassData>>({});
  const [deleteFormErrors, setDeleteFormErrors] = useState<Partial<{code: string}>>({});
  const [manageStudentFormErrors, setManageStudentFormErrors] = useState<Partial<{code: string}>>({});
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [teacherSubmitting, setTeacherSubmitting] = useState(false);
  const [classSubmitting, setClassSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [managingStudent, setManagingStudent] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      await loadStudents();
      await loadTeachers();
    };
    
    initializeData();
  }, []);

  // teachers 배열이 변경될 때마다 classes 다시 로드
  useEffect(() => {
    if (teachers.length > 0) {
      loadClassesWithTeachers(teachers);
    }
  }, [teachers]);

  // 학생 목록 불러오기
  const loadStudents = async () => {
    setLoading(true);
    try {
      // students/code 경로에서 학생 데이터 가져오기
      const studentsRef = ref(realtimeDb, 'students/code');
      const snapshot = await get(studentsRef);
      
      if (snapshot.exists()) {
        const studentsData = snapshot.val();
        console.log('가져온 학생 데이터:', studentsData);
        
                 const studentsArray = Object.entries(studentsData).map(([code, data]: [string, any]) => {
           console.log(`코드 ${code}의 데이터:`, data);
           return {
             code,
             name: data.name || '이름 없음',
             englishName: data.englishName || '영어 이름 없음',
             class: data.class || '',
             createdAt: data.createdAt || ''
           };
         });
        
        console.log('처리된 학생 배열:', studentsArray);
        setStudents(studentsArray);
      } else {
        console.log('학생 데이터가 없습니다.');
        setStudents([]);
      }
    } catch (error) {
      console.error('학생 목록 불러오기 실패:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // 선생님 목록 불러오기
  const loadTeachers = async () => {
    try {
      // teachers/code 경로에서 선생님 데이터 가져오기
      const teachersRef = ref(realtimeDb, 'teachers/code');
      const snapshot = await get(teachersRef);
      
      if (snapshot.exists()) {
        const teachersData = snapshot.val();
        console.log('가져온 선생님 데이터:', teachersData);
        
        const teachersArray = Object.entries(teachersData).map(([code, data]: [string, any]) => {
          console.log(`코드 ${code}의 선생님 데이터:`, data);
          return {
            code,
            name: data.name || '이름 없음',
            createdAt: data.createdAt || ''
          };
        });
        
        console.log('처리된 선생님 배열:', teachersArray);
        setTeachers(teachersArray);
      } else {
        console.log('선생님 데이터가 없습니다.');
        setTeachers([]);
      }
    } catch (error) {
      console.error('선생님 목록 불러오기 실패:', error);
      setTeachers([]);
    }
  };

  // 반 목록 불러오기 (teachers 배열 없이)
  const loadClasses = async () => {
    try {
      // classes 경로에서 반 데이터 가져오기
      const classesRef = ref(realtimeDb, 'classes');
      const snapshot = await get(classesRef);
      
      if (snapshot.exists()) {
        const classesData = snapshot.val();
        console.log('가져온 반 데이터:', classesData);
        
        const classesArray = Object.entries(classesData).map(([name, data]: [string, any]) => {
          console.log(`반 ${name}의 데이터:`, data);
          return {
            name,
            teacher: data.teacher || '',
            teacherName: '', // teachers 배열이 없으므로 빈 문자열로 설정
            createdAt: data.createdAt || ''
          };
        });
        
        console.log('처리된 반 배열:', classesArray);
        setClasses(classesArray);
      } else {
        console.log('반 데이터가 없습니다.');
        setClasses([]);
      }
    } catch (error) {
      console.error('반 목록 불러오기 실패:', error);
      setClasses([]);
    }
  };

  // teachers 배열과 함께 반 목록 불러오기
  const loadClassesWithTeachers = async (teachersArray: any[]) => {
    try {
      // classes 경로에서 반 데이터 가져오기
      const classesRef = ref(realtimeDb, 'classes');
      const snapshot = await get(classesRef);
      
      if (snapshot.exists()) {
        const classesData = snapshot.val();
        console.log('가져온 반 데이터 (teachers 포함):', classesData);
        
        const classesArray = Object.entries(classesData).map(([name, data]: [string, any]) => {
          console.log(`반 ${name}의 데이터:`, data);
          return {
            name,
            teacher: data.teacher || '',
            teacherName: data.teacher ? teachersArray.find(t => t.code === data.teacher)?.name || '' : '',
            createdAt: data.createdAt || ''
          };
        });
        
        console.log('처리된 반 배열 (teachers 포함):', classesArray);
        setClasses(classesArray);
      } else {
        console.log('반 데이터가 없습니다.');
        setClasses([]);
      }
    } catch (error) {
      console.error('반 목록 불러오기 실패:', error);
      setClasses([]);
    }
  };

  // 학생 폼 입력 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setStudentForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 제거
    if (formErrors[name as keyof StudentData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 선생님 폼 입력 처리
  const handleTeacherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTeacherForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 제거
    if (teacherFormErrors[name as keyof TeacherData]) {
      setTeacherFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 반 폼 입력 처리
  const handleClassInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setClassForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 제거
    if (classFormErrors[name as keyof ClassData]) {
      setClassFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 학생 폼 유효성 검사
  const validateForm = (): boolean => {
    const errors: Partial<StudentData> = {};
    
    if (!studentForm.name.trim()) {
      errors.name = '이름을 입력해주세요.';
    }
    
    if (!studentForm.englishName.trim()) {
      errors.englishName = '영어 이름을 입력해주세요.';
    }

    if (!studentForm.code?.trim()) {
      errors.code = '학생 코드를 입력해주세요.';
    }
    
    // class는 선택사항이므로 유효성 검사에서 제외
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 선생님 폼 유효성 검사
  const validateTeacherForm = (): boolean => {
    const errors: Partial<TeacherData> = {};
    
    if (!teacherForm.name.trim()) {
      errors.name = '이름을 입력해주세요.';
    }
    
    if (!teacherForm.code?.trim()) {
      errors.code = '선생님 코드를 입력해주세요.';
    }
    
    setTeacherFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

     // 반 폼 유효성 검사
   const validateClassForm = (): boolean => {
     const errors: Partial<ClassData> = {};
     
     if (!classForm.name.trim()) {
       errors.name = '반 이름을 입력해주세요.';
     }
     
     // Firebase 경로에 사용할 수 없는 문자 체크
     const invalidChars = /[.#$\[\]\/]/;
     if (invalidChars.test(classForm.name.trim())) {
       errors.name = '반 이름에 점(.), #, $, [, ], / 문자를 사용할 수 없습니다.';
     }
     
     setClassFormErrors(errors);
     return Object.keys(errors).length === 0;
   };



  // 학생 코드 중복 확인 함수
  const isCodeUnique = async (code: string): Promise<boolean> => {
    try {
      const studentRef = ref(realtimeDb, `students/code/${code}`);
      const snapshot = await get(studentRef);
      return !snapshot.exists();
    } catch (error) {
      console.error('학생 코드 중복 확인 실패:', error);
      return false;
    }
  };

  // 선생님 코드 중복 확인 함수
  const isTeacherCodeUnique = async (code: string): Promise<boolean> => {
    try {
      const teacherRef = ref(realtimeDb, `teachers/code/${code}`);
      const snapshot = await get(teacherRef);
      return !snapshot.exists();
    } catch (error) {
      console.error('선생님 코드 중복 확인 실패:', error);
      return false;
    }
  };

  // 학생 추가 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const studentCode = studentForm.code?.trim();
      
      if (!studentCode) {
        throw new Error('학생 코드를 입력해주세요.');
      }

      // 코드 중복 확인
      const isUnique = await isCodeUnique(studentCode);
      if (!isUnique) {
        alert('이미 존재하는 학생 코드입니다. 다른 코드를 입력해주세요.');
        return;
      }
      
             // 학생 데이터를 두 곳에 저장
       // 1. students/code/{코드} 아래에 전체 정보 저장
       const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
       await set(studentRef, {
         name: studentForm.name.trim(),
         englishName: studentForm.englishName.trim(),
         class: studentForm.class?.trim() || '',
         createdAt: new Date().toISOString()
       });
       
       // 2. students/name/{한글이름} 아래에 코드와 정보 저장
       const nameRef = ref(realtimeDb, `students/name/${studentForm.name.trim()}`);
       await set(nameRef, {
         code: studentCode,
         englishName: studentForm.englishName.trim(),
         class: studentForm.class?.trim() || '',
         createdAt: new Date().toISOString()
       });

       // 3. 반이 선택된 경우 해당 반 아래에 학생 추가, 아니면 "not assigned" 반에 추가
       const className = studentForm.class?.trim() || 'not assigned';
       
       // "not assigned" 반이 없으면 생성
       if (className === 'not assigned') {
         const notAssignedClassRef = ref(realtimeDb, `classes/not assigned`);
         const notAssignedSnapshot = await get(notAssignedClassRef);
         if (!notAssignedSnapshot.exists()) {
           await set(notAssignedClassRef, {
             teacher: '',
             createdAt: new Date().toISOString()
           });
         }
       }
       
       const classStudentRef = ref(realtimeDb, `classes/${className}/students/${studentCode}`);
       await set(classStudentRef, {
         name: studentForm.name.trim(),
         englishName: studentForm.englishName.trim(),
         addedAt: new Date().toISOString()
       });
      
             // 성공 후 폼 초기화 및 모달 닫기
       setStudentForm({
         name: '',
         englishName: '',
         class: '',
         code: ''
       });
      setFormErrors({});
      setShowAddStudentModal(false);
      
      // 학생 목록 새로고침
      await loadStudents();
      
      alert(`학생이 성공적으로 추가되었습니다!\n입력된 코드: ${studentCode}`);
    } catch (error) {
      console.error('학생 추가 실패:', error);
      alert(error instanceof Error ? error.message : '학생 추가에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  // 선생님 추가 제출
  const handleTeacherSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateTeacherForm()) {
      return;
    }
    
    setTeacherSubmitting(true);
    
    try {
      const teacherCode = teacherForm.code?.trim();
      
      if (!teacherCode) {
        throw new Error('선생님 코드를 입력해주세요.');
      }

      // 코드 중복 확인
      const isUnique = await isTeacherCodeUnique(teacherCode);
      if (!isUnique) {
        alert('이미 존재하는 선생님 코드입니다. 다른 코드를 입력해주세요.');
        return;
      }
      
      // 선생님 데이터를 두 곳에 저장
      // 1. teachers/code/{코드} 아래에 전체 정보 저장
      const teacherRef = ref(realtimeDb, `teachers/code/${teacherCode}`);
      await set(teacherRef, {
        name: teacherForm.name.trim(),
        createdAt: new Date().toISOString()
      });
      
      // 2. teachers/name/{한글이름} 아래에 코드와 정보 저장
      const nameRef = ref(realtimeDb, `teachers/name/${teacherForm.name.trim()}`);
      await set(nameRef, {
        code: teacherCode,
        createdAt: new Date().toISOString()
      });
      
      // 성공 후 폼 초기화 및 모달 닫기
      setTeacherForm({
        name: '',
        code: ''
      });
      setTeacherFormErrors({});
      setShowAddTeacherModal(false);
      
      // 선생님 목록 새로고침
      await loadTeachers();
      
      alert(`선생님이 성공적으로 추가되었습니다!\n입력된 코드: ${teacherCode}`);
    } catch (error) {
      console.error('선생님 추가 실패:', error);
      alert(error instanceof Error ? error.message : '선생님 추가에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setTeacherSubmitting(false);
    }
  };

  // 선생님 수정 제출
  const handleEditTeacherSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateTeacherForm()) {
      return;
    }
    
    setTeacherSubmitting(true);
    
    try {
      const teacherCode = teacherForm.code?.trim();
      
      if (!teacherCode) {
        throw new Error('선생님 코드를 입력해주세요.');
      }

      // 코드가 변경된 경우에만 중복 확인
      const originalCode = teacherForm.code;
      if (originalCode !== teacherCode) {
        const isUnique = await isTeacherCodeUnique(teacherCode);
        if (!isUnique) {
          alert('이미 존재하는 선생님 코드입니다. 다른 코드를 입력해주세요.');
          return;
        }
      }
      
      // 기존 선생님 데이터 읽기 (기존 이름 확인 및 classes 등 기존 데이터 보존)
      const teacherRef = ref(realtimeDb, `teachers/code/${teacherCode}`);
      const teacherSnapshot = await get(teacherRef);
      
      if (!teacherSnapshot.exists()) {
        throw new Error('선생님 정보를 찾을 수 없습니다.');
      }
      
      const existingTeacherData = teacherSnapshot.val();
      const oldName = existingTeacherData.name;
      const newName = teacherForm.name.trim();
      
      // 이름이 변경된 경우에만 처리
      if (oldName !== newName) {
        // 1. teachers/code/{코드} 경로에서 name만 업데이트 (기존 classes 등 데이터 보존)
        await update(teacherRef, {
          name: newName
        });
        
        // 2. 기존 이름 경로 삭제
        if (oldName) {
          const oldNameRef = ref(realtimeDb, `teachers/name/${oldName}`);
          await remove(oldNameRef);
        }
        
        // 3. 새로운 이름 경로에 코드 저장 (update 사용하여 기존 데이터 보존)
        const newNameRef = ref(realtimeDb, `teachers/name/${newName}`);
        const newNameSnapshot = await get(newNameRef);
        
        if (newNameSnapshot.exists()) {
          // 기존 데이터가 있으면 code만 업데이트
          await update(newNameRef, {
            code: teacherCode
          });
        } else {
          // 기존 데이터가 없으면 새로 생성
          await set(newNameRef, {
            code: teacherCode,
            createdAt: existingTeacherData.createdAt || new Date().toISOString()
          });
        }
      } else {
        // 이름이 변경되지 않은 경우에도 createdAt이 없으면 추가
        if (!existingTeacherData.createdAt) {
          await update(teacherRef, {
            createdAt: new Date().toISOString()
          });
        }
      }
      
      // 성공 후 모달 닫기
      setShowEditTeacherModal(false);
      
      // 선생님 목록 새로고침
      await loadTeachers();
      
      alert('선생님 정보가 성공적으로 수정되었습니다!');
    } catch (error) {
      console.error('선생님 수정 실패:', error);
      alert(error instanceof Error ? error.message : '선생님 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setTeacherSubmitting(false);
    }
  };

  // 반 추가 제출
  const handleClassSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateClassForm()) {
      return;
    }
    
    setClassSubmitting(true);
    
    try {
      // 반 이름을 키로 사용하여 데이터 저장
      const classRef = ref(realtimeDb, `classes/${classForm.name.trim()}`);
      
      await set(classRef, {
        teacher: classForm.teacher?.trim() || '',
        createdAt: new Date().toISOString()
      });

      // 담당 선생님이 선택된 경우 해당 선생님 데이터 아래에 반 정보 추가
      if (classForm.teacher?.trim()) {
        const teacherRef = ref(realtimeDb, `teachers/code/${classForm.teacher.trim()}/classes/${classForm.name.trim()}`);
        await set(teacherRef, {
          name: classForm.name.trim(),
          addedAt: new Date().toISOString()
        });
      }
      
      // 성공 후 폼 초기화 및 모달 닫기
      setClassForm({
        name: '',
        teacher: ''
      });
      setClassFormErrors({});
      setShowAddClassModal(false);
      
      // 반 목록 새로고침
      await loadClassesWithTeachers(teachers);
      
      alert('반이 성공적으로 추가되었습니다!');
    } catch (error) {
      console.error('반 추가 실패:', error);
      alert('반 추가에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setClassSubmitting(false);
    }
  };

  // 반 수정 제출
  const handleEditClassSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateClassForm()) {
      return;
    }
    
    setClassSubmitting(true);
    
    try {
      const newClassName = classForm.name.trim();
      const newTeacher = classForm.teacher?.trim() || '';
      
      // 기존 반 정보 가져오기
      const originalClassRef = ref(realtimeDb, `classes/${originalClassName}`);
      const originalSnapshot = await get(originalClassRef);
      
      if (!originalSnapshot.exists()) {
        alert('수정할 반을 찾을 수 없습니다.');
        return;
      }
      
      const originalData = originalSnapshot.val();
      const originalTeacher = originalData.teacher || '';
      
      // 반 이름이 변경된 경우
      if (originalClassName !== newClassName) {
        // 1. 기존 반의 학생들 가져오기
        const originalStudentsRef = ref(realtimeDb, `classes/${originalClassName}/students`);
        const studentsSnapshot = await get(originalStudentsRef);
        
        // 2. 새 반 생성
        const newClassRef = ref(realtimeDb, `classes/${newClassName}`);
        await set(newClassRef, {
          teacher: newTeacher,
          createdAt: originalData.createdAt || new Date().toISOString()
        });
        
        // 3. 학생들을 새 반으로 이동
        if (studentsSnapshot.exists()) {
          const studentsData = studentsSnapshot.val();
          const newStudentsRef = ref(realtimeDb, `classes/${newClassName}/students`);
          await set(newStudentsRef, studentsData);
          
          // 4. 각 학생의 개별 데이터에서 반 정보 업데이트
          for (const [studentCode, studentData] of Object.entries(studentsData)) {
            const studentInfo = studentData as any;
            // students/code/{코드}에서 반 정보 업데이트
            const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
            const studentSnapshot = await get(studentRef);
            if (studentSnapshot.exists()) {
              const currentStudentData = studentSnapshot.val();
              await set(studentRef, {
                ...currentStudentData,
                class: newClassName
              });
            }
            
            // students/name/{이름}에서도 반 정보 업데이트
            if (studentInfo.name) {
              const studentNameRef = ref(realtimeDb, `students/name/${studentInfo.name}`);
              const nameSnapshot = await get(studentNameRef);
              if (nameSnapshot.exists()) {
                const nameData = nameSnapshot.val();
                await set(studentNameRef, {
                  ...nameData,
                  class: newClassName
                });
              }
            }
          }
          
          // 5. 기존 반 삭제
          await remove(originalStudentsRef);
        }
        
        // 6. 기존 반 삭제
        await remove(originalClassRef);
        
        // 7. 기존 담당 선생님에서 반 정보 제거
        if (originalTeacher) {
          const oldTeacherRef = ref(realtimeDb, `teachers/code/${originalTeacher}/classes/${originalClassName}`);
          await remove(oldTeacherRef);
        }
        
        // 8. 새 담당 선생님에 반 정보 추가
        if (newTeacher) {
          const newTeacherRef = ref(realtimeDb, `teachers/code/${newTeacher}/classes/${newClassName}`);
          await set(newTeacherRef, {
            name: newClassName,
            addedAt: new Date().toISOString()
          });
        }
      } else {
        // 반 이름이 변경되지 않은 경우 (담당 선생님만 변경)
        await set(originalClassRef, {
          teacher: newTeacher,
          createdAt: originalData.createdAt || new Date().toISOString()
        });
        
        // 기존 담당 선생님에서 반 정보 제거
        if (originalTeacher && originalTeacher !== newTeacher) {
          const oldTeacherRef = ref(realtimeDb, `teachers/code/${originalTeacher}/classes/${originalClassName}`);
          await remove(oldTeacherRef);
        }
        
        // 새 담당 선생님에 반 정보 추가
        if (newTeacher) {
          const newTeacherRef = ref(realtimeDb, `teachers/code/${newTeacher}/classes/${originalClassName}`);
          await set(newTeacherRef, {
            name: originalClassName,
            addedAt: new Date().toISOString()
          });
        }
      }
      
      // 성공 후 모달 닫기
      setShowEditClassModal(false);
      
      // 반 목록 새로고침
      await loadClassesWithTeachers(teachers);
      
      alert('반 정보가 성공적으로 수정되었습니다!');
    } catch (error) {
      console.error('반 수정 실패:', error);
      alert('반 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setClassSubmitting(false);
    }
  };

     // 학생 추가 모달 닫기
   const handleCloseModal = () => {
     setShowAddStudentModal(false);
     setStudentForm({
       name: '',
       englishName: '',
       class: '',
       code: ''
     });
     setFormErrors({});
   };

     // 학생 수정 모달 열기
   const openEditStudentModal = (student: any) => {
     setEditStudentForm({
       name: student.name,
       englishName: student.englishName,
       class: student.class || '',
       code: student.code
     });
     setEditFormErrors({});
     setShowEditStudentModal(true);
   };

     // 학생 수정 모달 닫기
   const handleCloseEditModal = () => {
     setShowEditStudentModal(false);
     setEditStudentForm({
       name: '',
       englishName: '',
       class: '',
       code: ''
     });
     setEditFormErrors({});
   };

  // 학생 수정 폼 입력 처리
  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditStudentForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 제거
    if (editFormErrors[name as keyof StudentData]) {
      setEditFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 학생 수정 폼 유효성 검사
  const validateEditForm = (): boolean => {
    const errors: Partial<StudentData> = {};
    
    if (!editStudentForm.name.trim()) {
      errors.name = '이름을 입력해주세요.';
    }
    
    if (!editStudentForm.englishName.trim()) {
      errors.englishName = '영어 이름을 입력해주세요.';
    }

    if (!editStudentForm.code?.trim()) {
      errors.code = '학생 코드를 입력해주세요.';
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 학생 수정 제출
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setEditing(true);
    
    try {
      const studentCode = editStudentForm.code?.trim();
      
      if (!studentCode) {
        throw new Error('학생 코드를 입력해주세요.');
      }

      // 코드가 변경된 경우에만 중복 확인
      const originalCode = editStudentForm.code;
      if (originalCode !== studentCode) {
        const isUnique = await isCodeUnique(studentCode);
        if (!isUnique) {
          alert('이미 존재하는 학생 코드입니다. 다른 코드를 입력해주세요.');
          return;
        }
      }

      // 기존 학생 정보 가져오기 (반 변경 처리용)
      const existingStudentRef = ref(realtimeDb, `students/code/${studentCode}`);
      const existingSnapshot = await get(existingStudentRef);
      const existingClass = existingSnapshot.exists() ? existingSnapshot.val().class : '';
      
             // 학생 데이터 업데이트
       // 1. students/code/{코드} 아래에 전체 정보 저장
       const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
       await set(studentRef, {
         name: editStudentForm.name.trim(),
         englishName: editStudentForm.englishName.trim(),
         class: editStudentForm.class?.trim() || '',
         createdAt: new Date().toISOString()
       });
       
       // 2. students/name/{한글이름} 아래에 코드와 정보 저장
       const nameRef = ref(realtimeDb, `students/name/${editStudentForm.name.trim()}`);
       await set(nameRef, {
         code: studentCode,
         englishName: editStudentForm.englishName.trim(),
         class: editStudentForm.class?.trim() || '',
         createdAt: new Date().toISOString()
       });

       // 3. 반 변경 처리
       const newClass = editStudentForm.class?.trim() || 'not assigned';
       
       // 기존 반에서 학생 제거
       if (existingClass && existingClass !== newClass) {
         const oldClassStudentRef = ref(realtimeDb, `classes/${existingClass}/students/${studentCode}`);
         await remove(oldClassStudentRef);
       }
       
       // "not assigned" 반이 없으면 생성
       if (newClass === 'not assigned') {
         const notAssignedClassRef = ref(realtimeDb, `classes/not assigned`);
         const notAssignedSnapshot = await get(notAssignedClassRef);
         if (!notAssignedSnapshot.exists()) {
           await set(notAssignedClassRef, {
             teacher: '',
             createdAt: new Date().toISOString()
           });
         }
       }
       
       // 새 반에 학생 추가
       const newClassStudentRef = ref(realtimeDb, `classes/${newClass}/students/${studentCode}`);
       await set(newClassStudentRef, {
         name: editStudentForm.name.trim(),
         englishName: editStudentForm.englishName.trim(),
         addedAt: new Date().toISOString()
       });
      
      // 성공 후 모달 닫기
      setShowEditStudentModal(false);
      
      // 학생 목록 새로고침
      await loadStudents();
      
      alert('학생 정보가 성공적으로 수정되었습니다!');
    } catch (error) {
      console.error('학생 수정 실패:', error);
      alert(error instanceof Error ? error.message : '학생 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setEditing(false);
    }
  };

  // 선생님 모달 닫기
  const handleCloseTeacherModal = () => {
    setShowAddTeacherModal(false);
    setTeacherForm({
      name: '',
      code: ''
    });
    setTeacherFormErrors({});
  };

  // 선생님 수정 모달 열기
  const openEditTeacherModal = (teacher: any) => {
    setTeacherForm({
      name: teacher.name,
      code: teacher.code
    });
    setTeacherFormErrors({});
    setShowEditTeacherModal(true);
  };

  // 선생님 수정 모달 닫기
  const handleCloseEditTeacherModal = () => {
    setShowEditTeacherModal(false);
    setTeacherForm({
      name: '',
      code: ''
    });
    setTeacherFormErrors({});
  };

  // 반 모달 닫기
  const handleCloseClassModal = () => {
    setShowAddClassModal(false);
    setClassForm({
      name: '',
      teacher: ''
    });
    setClassFormErrors({});
  };

  // 반 수정 모달 열기
  const openEditClassModal = (classItem: any) => {
    setClassForm({
      name: classItem.name,
      teacher: classItem.teacher || ''
    });
    setClassFormErrors({});
    setShowEditClassModal(true);
    // 기존 반 이름 저장 (이름 변경 처리용)
    setOriginalClassName(classItem.name);
  };

  // 반 수정 모달 닫기
  const handleCloseEditClassModal = () => {
    setShowEditClassModal(false);
    setClassForm({
      name: '',
      teacher: ''
    });
    setClassFormErrors({});
    setOriginalClassName('');
  };

  // 학생 목록 모달 열기
  const openStudentListModal = async (className: string) => {
    try {
      // 해당 반에 소속된 학생들 찾기 (새로운 구조 사용)
      const classStudentsRef = ref(realtimeDb, `classes/${className}/students`);
      const snapshot = await get(classStudentsRef);
      
      if (snapshot.exists()) {
        const studentsData = snapshot.val();
        const classStudents = Object.entries(studentsData)
          .map(([code, data]: [string, any]) => ({
            id: code,
            code,
            ...data
          }));
        
        setSelectedClassStudents(classStudents);
        setSelectedClassName(className);
        setShowStudentListModal(true);
      } else {
        setSelectedClassStudents([]);
        setSelectedClassName(className);
        setShowStudentListModal(true);
      }
    } catch (error) {
      console.error('학생 목록 불러오기 실패:', error);
      alert('학생 목록을 불러오는데 실패했습니다.');
    }
  };

     // 학생 목록 모달 닫기
   const handleCloseStudentListModal = () => {
     setShowStudentListModal(false);
     setSelectedClassStudents([]);
     setSelectedClassName('');
   };

   // 선생님 담당반 모달 열기
   const openTeacherClassesModal = async (teacher: any) => {
     try {
       // 해당 선생님이 담당하는 반들 찾기
       const teacherClassesRef = ref(realtimeDb, `teachers/code/${teacher.code}/classes`);
       const snapshot = await get(teacherClassesRef);
       
       if (snapshot.exists()) {
         const classesData = snapshot.val();
         const teacherClasses = Object.entries(classesData)
           .map(([name, data]: [string, any]) => ({
             id: name,
             name,
             ...data
           }));
         
         setSelectedTeacherClasses(teacherClasses);
         setSelectedTeacherName(teacher.name);
         setShowTeacherClassesModal(true);
       } else {
         setSelectedTeacherClasses([]);
         setSelectedTeacherName(teacher.name);
         setShowTeacherClassesModal(true);
       }
     } catch (error) {
       console.error('선생님 담당반 목록 불러오기 실패:', error);
       alert('선생님 담당반 목록을 불러오는데 실패했습니다.');
     }
   };

   // 선생님 담당반 모달 닫기
   const handleCloseTeacherClassesModal = () => {
     setShowTeacherClassesModal(false);
     setSelectedTeacherClasses([]);
     setSelectedTeacherName('');
   };

  // 삭제 폼 입력 처리
  const handleDeleteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDeleteForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 제거
    if (deleteFormErrors[name as keyof {code: string}]) {
      setDeleteFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 삭제 폼 유효성 검사
  const validateDeleteForm = (): boolean => {
    const errors: Partial<{code: string}> = {};
    
    if (!deleteForm.code.trim()) {
      errors.code = '코드를 입력해주세요.';
    }
    
    setDeleteFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 학생 코드로 조회
  const findStudentByCode = async (code: string) => {
    try {
      const studentRef = ref(realtimeDb, `students/code/${code}`);
      const snapshot = await get(studentRef);
      
      if (snapshot.exists()) {
        const studentData = snapshot.val();
        return {
          code,
          ...studentData
        };
      }
      return null;
    } catch (error) {
      console.error('학생 조회 실패:', error);
      return null;
    }
  };

  // 선생님 코드로 조회
  const findTeacherByCode = async (code: string) => {
    try {
      const teacherRef = ref(realtimeDb, `teachers/code/${code}`);
      const snapshot = await get(teacherRef);
      
      if (snapshot.exists()) {
        const teacherData = snapshot.val();
        return {
          code,
          ...teacherData
        };
      }
      return null;
    } catch (error) {
      console.error('선생님 조회 실패:', error);
      return null;
    }
  };

  // 반 이름으로 조회
  const findClassByName = async (name: string) => {
    try {
      const classRef = ref(realtimeDb, `classes/${name}`);
      const snapshot = await get(classRef);
      
      if (snapshot.exists()) {
        const classData = snapshot.val();
        return {
          name,
          ...classData
        };
      }
      return null;
    } catch (error) {
      console.error('반 조회 실패:', error);
      return null;
    }
  };

  // 학생 삭제
  const handleDeleteStudent = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    
    try {
      const { code, name, class: studentClass } = itemToDelete;
      
      // 1. students/code/{코드}에서 삭제
      const studentCodeRef = ref(realtimeDb, `students/code/${code}`);
      await remove(studentCodeRef);
      
      // 2. students/name/{이름}에서 삭제
      const studentNameRef = ref(realtimeDb, `students/name/${name}`);
      await remove(studentNameRef);

      // 3. 반에 소속된 경우 해당 반에서도 학생 제거
      if (studentClass) {
        const classStudentRef = ref(realtimeDb, `classes/${studentClass}/students/${code}`);
        await remove(classStudentRef);
      }
      
      // 성공 후 모달 닫기 및 폼 초기화
      setShowDeleteStudentModal(false);
      setDeleteForm({ code: '' });
      setItemToDelete(null);
      setDeleteFormErrors({});
      
      // 학생 목록 새로고침
      await loadStudents();
      
      alert('학생이 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('학생 삭제 실패:', error);
      alert('학생 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeleting(false);
    }
  };

  // 선생님 삭제
  const handleDeleteTeacher = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    
    try {
      const { code, name } = itemToDelete;
      
      // 1. teachers/code/{코드}에서 삭제
      const teacherCodeRef = ref(realtimeDb, `teachers/code/${code}`);
      await remove(teacherCodeRef);
      
      // 2. teachers/name/{이름}에서 삭제
      const teacherNameRef = ref(realtimeDb, `teachers/name/${name}`);
      await remove(teacherNameRef);
      
      // 성공 후 모달 닫기 및 폼 초기화
      setShowDeleteTeacherModal(false);
      setDeleteForm({ code: '' });
      setItemToDelete(null);
      setDeleteFormErrors({});
      
      // 선생님 목록 새로고침
      await loadTeachers();
      
      alert('선생님이 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('선생님 삭제 실패:', error);
      alert('선생님 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeleting(false);
    }
  };

  // 학생 삭제 모달 열기
  const openDeleteStudentModal = () => {
    setShowDeleteStudentModal(true);
    setDeleteForm({ code: '' });
    setItemToDelete(null);
    setDeleteFormErrors({});
  };

  // 선생님 삭제 모달 열기
  const openDeleteTeacherModal = () => {
    setShowDeleteTeacherModal(true);
    setDeleteForm({ code: '' });
    setItemToDelete(null);
    setDeleteFormErrors({});
  };

  // 반 삭제 모달 열기
  const openDeleteClassModal = () => {
    setShowDeleteClassModal(true);
    setDeleteForm({ code: '' });
    setItemToDelete(null);
    setDeleteFormErrors({});
  };

  // 학생 삭제 검색
  const searchStudentForDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateDeleteForm()) {
      return;
    }
    
    const student = await findStudentByCode(deleteForm.code.trim());
    
    if (student) {
      setItemToDelete(student);
    } else {
      alert('해당 코드의 학생을 찾을 수 없습니다.');
    }
  };

  // 선생님 삭제 검색
  const searchTeacherForDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateDeleteForm()) {
      return;
    }
    
    const teacher = await findTeacherByCode(deleteForm.code.trim());
    
    if (teacher) {
      setItemToDelete(teacher);
    } else {
      alert('해당 코드의 선생님을 찾을 수 없습니다.');
    }
  };

  // 반 삭제 검색
  const searchClassForDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateDeleteForm()) {
      return;
    }
    
    const classItem = await findClassByName(deleteForm.code.trim());
    
    if (classItem) {
      setItemToDelete(classItem);
    } else {
      alert('해당 이름의 반을 찾을 수 없습니다.');
    }
  };

  // 반 삭제
  const handleDeleteClass = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    
    try {
      const { name } = itemToDelete;
      
      // 1. 해당 반에 소속된 학생들의 반 정보 제거
      const classStudentsRef = ref(realtimeDb, `classes/${name}/students`);
      const studentsSnapshot = await get(classStudentsRef);
      
      if (studentsSnapshot.exists()) {
        const studentsData = studentsSnapshot.val();
        const studentCodes = Object.keys(studentsData);
        
        // 각 학생의 반 정보를 "not assigned"로 변경
        for (const studentCode of studentCodes) {
          // students/code/{코드}에서 반 정보를 "not assigned"로 변경
          const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
          const studentSnapshot = await get(studentRef);
          
          if (studentSnapshot.exists()) {
            const studentData = studentSnapshot.val();
            
            await set(studentRef, {
              ...studentData,
              class: "not assigned"
            });
            
            // students/name/{이름}에서도 반 정보를 "not assigned"로 변경
            if (studentData.name) {
              const studentNameRef = ref(realtimeDb, `students/name/${studentData.name}`);
              const nameSnapshot = await get(studentNameRef);
              
              if (nameSnapshot.exists()) {
                const nameData = nameSnapshot.val();
                
                await set(studentNameRef, {
                  ...nameData,
                  class: "not assigned"
                });
              }
            }
          }
        }
      }

      // 2. 담당 선생님 데이터에서 반 정보 제거
      const classRef = ref(realtimeDb, `classes/${name}`);
      const classSnapshot = await get(classRef);
      
      if (classSnapshot.exists()) {
        const classData = classSnapshot.val();
        
        // 방법 1: 클래스 데이터에 teacher 정보가 있는 경우
        if (classData.teacher) {
          const teacherClassRef = ref(realtimeDb, `teachers/code/${classData.teacher}/classes/${name}`);
          await remove(teacherClassRef);
        }
        
        // 방법 2: 모든 선생님에서 해당 반을 찾아서 제거 (더 안전한 방법)
        const teachersRef = ref(realtimeDb, 'teachers/code');
        const teachersSnapshot = await get(teachersRef);
        
        if (teachersSnapshot.exists()) {
          const teachersData = teachersSnapshot.val();
          const teacherCodes = Object.keys(teachersData);
          
          for (const teacherCode of teacherCodes) {
            const teacherClassRef = ref(realtimeDb, `teachers/code/${teacherCode}/classes/${name}`);
            const teacherClassSnapshot = await get(teacherClassRef);
            
            if (teacherClassSnapshot.exists()) {
              await remove(teacherClassRef);
              console.log(`선생님 ${teacherCode}에서 반 ${name} 제거 완료`);
            }
          }
        }
      }
      
      // 3. classes/{name}에서 삭제
      await remove(classRef);
      
      // 성공 후 모달 닫기 및 폼 초기화
      setShowDeleteClassModal(false);
      setDeleteForm({ code: '' });
      setItemToDelete(null);
      setDeleteFormErrors({});
      
      // 목록 새로고침
      await loadClassesWithTeachers(teachers);
      await loadStudents();
      
      alert('반이 성공적으로 삭제되었습니다. 해당 반에 소속된 학생들의 반 정보가 "not assigned"로 변경되었습니다.');
    } catch (error) {
      console.error('반 삭제 실패:', error);
      alert('반 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setDeleting(false);
    }
  };

  // 삭제 모달 닫기
  const closeDeleteModal = () => {
    setShowDeleteStudentModal(false);
    setShowDeleteTeacherModal(false);
    setShowDeleteClassModal(false);
    setDeleteForm({ code: '' });
    setItemToDelete(null);
    setDeleteFormErrors({});
  };

     // 선생님이 관리하는 학생 목록 불러오기
   const loadManagedStudents = async (teacherCode: string) => {
     try {
       const managedStudentsRef = ref(realtimeDb, `teachers/code/${teacherCode}/students`);
       const snapshot = await get(managedStudentsRef);
      
      if (snapshot.exists()) {
        const managedStudentsData = snapshot.val();
        const managedStudentsArray = Object.entries(managedStudentsData).map(([code, data]: [string, any]) => ({
          code,
          ...data
        }));
        setManagedStudents(managedStudentsArray);
      } else {
        setManagedStudents([]);
      }
    } catch (error) {
      console.error('관리 학생 목록 불러오기 실패:', error);
      setManagedStudents([]);
    }
  };

  // 선생님 관리 모달 열기
  const openTeacherManageModal = async (teacher: any) => {
    setSelectedTeacher(teacher);
    setShowTeacherManageModal(true);
    setManageStudentForm({ code: '' });
    setManageStudentFormErrors({});
    await loadManagedStudents(teacher.code);
  };

  // 선생님 관리 모달 닫기
  const closeTeacherManageModal = () => {
    setShowTeacherManageModal(false);
    setSelectedTeacher(null);
    setManageStudentForm({ code: '' });
    setManageStudentFormErrors({});
    setManagedStudents([]);
  };

  // 학생 관리 폼 입력 처리
  const handleManageStudentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setManageStudentForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 제거
    if (manageStudentFormErrors[name as keyof {code: string}]) {
      setManageStudentFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 학생 관리 폼 유효성 검사
  const validateManageStudentForm = (): boolean => {
    const errors: Partial<{code: string}> = {};
    
    if (!manageStudentForm.code.trim()) {
      errors.code = '학생 코드를 입력해주세요.';
    }
    
    setManageStudentFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 학생을 선생님 관리 하에 추가
  const addStudentToTeacher = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateManageStudentForm() || !selectedTeacher) {
      return;
    }
    
    setManagingStudent(true);
    
    try {
      const studentCode = manageStudentForm.code.trim();
      
      // 1. 학생이 존재하는지 확인
      const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
      const studentSnapshot = await get(studentRef);
      
      if (!studentSnapshot.exists()) {
        alert('해당 코드의 학생을 찾을 수 없습니다.');
        return;
      }
      
      const studentData = studentSnapshot.val();
      
             // 2. 이미 해당 선생님이 관리하고 있는지 확인
       const existingStudentRef = ref(realtimeDb, `teachers/code/${selectedTeacher.code}/students/${studentCode}`);
       const existingSnapshot = await get(existingStudentRef);
       
       if (existingSnapshot.exists()) {
         alert('이미 해당 선생님이 관리하고 있는 학생입니다.');
         return;
       }
       
               // 3. 선생님 관리 하에 학생 추가
        await set(existingStudentRef, {
          name: studentData.name,
          englishName: studentData.englishName,
          class: studentData.class || '',
          addedAt: new Date().toISOString()
        });
      
      // 4. 학생 정보에 담당 선생님 업데이트
      const studentUpdateRef = ref(realtimeDb, `students/code/${studentCode}`);
      await set(studentUpdateRef, {
        ...studentData,
        nativeTeacher: selectedTeacher.name
      });
      
      // 5. 학생 이름 경로도 업데이트
      const studentNameUpdateRef = ref(realtimeDb, `students/name/${studentData.name}`);
      await set(studentNameUpdateRef, {
        code: studentCode,
        englishName: studentData.englishName,
        class: studentData.class || '',
        createdAt: studentData.createdAt
      });
      
      // 성공 후 폼 초기화 및 목록 새로고침
      setManageStudentForm({ code: '' });
      await loadManagedStudents(selectedTeacher.code);
      await loadStudents(); // 전체 학생 목록도 새로고침
      
      alert('학생이 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('학생 추가 실패:', error);
      alert('학생 추가에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setManagingStudent(false);
    }
  };

  // 선생님 관리 하에서 학생 제거
  const removeStudentFromTeacher = async (studentCode: string) => {
    if (!selectedTeacher) return;
    
    if (!window.confirm('정말로 이 학생을 관리 목록에서 제거하시겠습니까?')) {
      return;
    }
    
    try {
             // 1. 선생님 관리 하에서 학생 제거
       const managedStudentRef = ref(realtimeDb, `teachers/code/${selectedTeacher.code}/students/${studentCode}`);
       await remove(managedStudentRef);
      
      // 2. 학생 정보에서 담당 선생님 제거
      const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
      const studentSnapshot = await get(studentRef);
      
      if (studentSnapshot.exists()) {
        const studentData = studentSnapshot.val();
        const { nativeTeacher, ...studentDataWithoutTeacher } = studentData;
        
        await set(studentRef, studentDataWithoutTeacher);
        
        // 3. 학생 이름 경로도 업데이트
        const studentNameRef = ref(realtimeDb, `students/name/${studentData.name}`);
        await set(studentNameRef, {
          code: studentCode,
          englishName: studentData.englishName,
          class: studentData.class || '',
          createdAt: studentData.createdAt
        });
      }
      
      // 목록 새로고침
      await loadManagedStudents(selectedTeacher.code);
      await loadStudents();
      
      alert('학생이 관리 목록에서 제거되었습니다.');
    } catch (error) {
      console.error('학생 제거 실패:', error);
      alert('학생 제거에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 학생 데이터 정리 함수
  const handleCleanupStudentData = async () => {
    if (!window.confirm('모든 학생 데이터를 확인하고 다음 작업을 수행합니다:\n\n1. 존재하지 않는 반에 소속된 학생들을 "not assigned" 반으로 이동\n2. 반에 소속되어 있지만 실제 반 데이터에 추가되지 않은 학생들을 해당 반에 추가\n\n계속하시겠습니까?')) {
      return;
    }

    setLoading(true);
    
    try {
      // 1. 모든 반 목록 가져오기
      const classesRef = ref(realtimeDb, 'classes');
      const classesSnapshot = await get(classesRef);
      const existingClasses = classesSnapshot.exists() ? Object.keys(classesSnapshot.val()) : [];
      
      console.log('존재하는 반 목록:', existingClasses);
      
      // 2. 모든 학생 데이터 가져오기 (code 경로에서)
      const studentsRef = ref(realtimeDb, 'students/code');
      const studentsSnapshot = await get(studentsRef);
      
      if (!studentsSnapshot.exists()) {
        alert('학생 데이터가 없습니다.');
        return;
      }
      
      const studentsData = studentsSnapshot.val();
      const studentCodes = Object.keys(studentsData);
      let movedCount = 0;
      let addedCount = 0;
      let errorCount = 0;
      
      console.log(`총 ${studentCodes.length}명의 학생 데이터를 확인합니다.`);
      
      // 3. 각 학생의 반 정보 확인 및 수정
      for (const studentCode of studentCodes) {
        try {
          const studentData = studentsData[studentCode];
          const studentClass = studentData.class || '';
          
          // 반이 비어있거나 존재하지 않는 반인 경우
          if (!studentClass || !existingClasses.includes(studentClass)) {
            console.log(`학생 ${studentCode} (${studentData.name})의 반 "${studentClass}"이 존재하지 않습니다. "not assigned"로 이동합니다.`);
            
            // "not assigned" 반이 없으면 생성
            const notAssignedClassRef = ref(realtimeDb, 'classes/not assigned');
            const notAssignedSnapshot = await get(notAssignedClassRef);
            if (!notAssignedSnapshot.exists()) {
              await set(notAssignedClassRef, {
                teacher: '',
                createdAt: new Date().toISOString()
              });
              console.log('"not assigned" 반을 생성했습니다.');
            }
            
            // 기존 반에서 학생 제거 (존재하는 경우에만)
            if (studentClass && existingClasses.includes(studentClass)) {
              const oldClassStudentRef = ref(realtimeDb, `classes/${studentClass}/students/${studentCode}`);
              await remove(oldClassStudentRef);
            }
            
            // "not assigned" 반에 학생 추가
            const notAssignedStudentRef = ref(realtimeDb, `classes/not assigned/students/${studentCode}`);
            await set(notAssignedStudentRef, {
              name: studentData.name,
              englishName: studentData.englishName,
              addedAt: new Date().toISOString()
            });
            
            // students/code/{코드}에서 반 정보 업데이트
            await set(ref(realtimeDb, `students/code/${studentCode}`), {
              ...studentData,
              class: 'not assigned'
            });
            
            // students/name/{이름}에서도 반 정보 업데이트
            if (studentData.name) {
              const studentNameRef = ref(realtimeDb, `students/name/${studentData.name}`);
              const nameSnapshot = await get(studentNameRef);
              if (nameSnapshot.exists()) {
                const nameData = nameSnapshot.val();
                await set(studentNameRef, {
                  ...nameData,
                  class: 'not assigned'
                });
              }
            }
            
            movedCount++;
          } else {
            // 반이 존재하는 경우, 해당 반에 학생이 실제로 추가되어 있는지 확인
            const classStudentRef = ref(realtimeDb, `classes/${studentClass}/students/${studentCode}`);
            const classStudentSnapshot = await get(classStudentRef);
            
            if (!classStudentSnapshot.exists()) {
              console.log(`학생 ${studentCode} (${studentData.name})이 반 "${studentClass}"에 소속되어 있지만 실제 반 데이터에 추가되지 않았습니다. 추가합니다.`);
              
              // 해당 반에 학생 추가
              await set(classStudentRef, {
                name: studentData.name,
                englishName: studentData.englishName,
                addedAt: new Date().toISOString()
              });
              
              addedCount++;
            }
          }
        } catch (error) {
          console.error(`학생 ${studentCode} 처리 중 오류:`, error);
          errorCount++;
        }
      }
      
      // 4. 반 목록 새로고침
      await loadClassesWithTeachers(teachers);
      
      // 5. 결과 알림
      const totalFixed = movedCount + addedCount;
      if (totalFixed > 0) {
        let message = `학생 데이터 정리가 완료되었습니다!\n\n`;
        if (movedCount > 0) {
          message += `- ${movedCount}명의 학생을 "not assigned" 반으로 이동했습니다.\n`;
        }
        if (addedCount > 0) {
          message += `- ${addedCount}명의 학생을 해당 반에 추가했습니다.\n`;
        }
        if (errorCount > 0) {
          message += `- ${errorCount}개의 오류가 발생했습니다.`;
        }
        alert(message);
      } else {
        alert('모든 학생 데이터가 정상적으로 정리되어 있습니다.\n수정된 데이터가 없습니다.');
      }
      
    } catch (error) {
      console.error('학생 데이터 정리 실패:', error);
      alert('학생 데이터 정리에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // 선생님 데이터 정리 함수
  const handleCleanupTeacherData = async () => {
    if (!window.confirm('선생님 데이터를 정리하시겠습니까?\n각 반의 담당 선생님 정보를 확인하고 복구합니다.')) {
      return;
    }

    setLoading(true);
    
    try {
      let fixedCount = 0;
      let checkedCount = 0;
      
      // 1. 모든 classes 순회
      const classesRef = ref(realtimeDb, 'classes');
      const classesSnapshot = await get(classesRef);
      
      if (!classesSnapshot.exists()) {
        alert('반 데이터가 없습니다.');
        return;
      }
      
      const classesData = classesSnapshot.val();
      
      // 2. 각 반을 확인하여 teacher 필드에서 선생님 코드 읽기
      for (const [className, classData] of Object.entries(classesData)) {
        checkedCount++;
        const classInfo = classData as any;
        const teacherCode = classInfo.teacher;
        
        // teacher 필드가 없거나 비어있으면 스킵
        if (!teacherCode || teacherCode.trim() === '') {
          continue;
        }
        
        // 3. 해당 선생님 코드에 해당하는 선생님 데이터 아래에 반이 제대로 추가되어 있는지 확인
        const teacherClassesRef = ref(realtimeDb, `teachers/code/${teacherCode}/classes/${className}`);
        const teacherClassSnapshot = await get(teacherClassesRef);
        
        // 4. 없으면 추가
        if (!teacherClassSnapshot.exists()) {
          await set(teacherClassesRef, {
            name: className,
            addedAt: classInfo.addedAt || classInfo.createdAt || new Date().toISOString()
          });
          fixedCount++;
          
          console.log(`반 ${className}을 선생님 ${teacherCode}에 추가`);
        }
      }
      
      // 6. 선생님 목록 새로고침
      await loadTeachers();
      
      // 7. 결과 알림
      if (fixedCount > 0) {
        alert(`선생님 데이터 정리가 완료되었습니다!\n\n- 확인한 반: ${checkedCount}개\n- 복구한 반 정보: ${fixedCount}개`);
      } else {
        alert(`모든 선생님 데이터가 정상적으로 정리되어 있습니다.\n\n- 확인한 반: ${checkedCount}개\n- 수정된 데이터가 없습니다.`);
      }
      
    } catch (error) {
      console.error('선생님 데이터 정리 실패:', error);
      alert('선생님 데이터 정리에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <h2 className="dashboard-title">관리자 대시보드</h2>
      
             {/* 탭 네비게이션 */}
       <div className="tab-navigation">
         <button 
           className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
           onClick={() => setActiveTab('students')}
         >
           학생 관리
         </button>
         <button 
           className={`tab-button ${activeTab === 'teachers' ? 'active' : ''}`}
           onClick={() => setActiveTab('teachers')}
         >
           선생님 관리
         </button>
        <button 
          className={`tab-button ${activeTab === 'classes' ? 'active' : ''}`}
          onClick={() => setActiveTab('classes')}
        >
          반 관리
        </button>
        <button 
          className={`tab-button ${activeTab === 'classesTest' ? 'active' : ''}`}
          onClick={() => setActiveTab('classesTest')}
        >
          반관리Test
        </button>
      </div>

      {/* 학생 관리 탭 */}
      {activeTab === 'students' && (
        <div className="tab-content">
          <div className="tab-header">
            <h3>학생 관리</h3>
            <div className="tab-actions">
              <button 
                onClick={() => setShowAddStudentModal(true)}
                className="add-button"
              >
                + 학생 추가
              </button>
              <button 
                onClick={handleCleanupStudentData}
                className="cleanup-button"
                disabled={loading}
              >
                🔧 학생 데이터 정리
              </button>
              <button 
                onClick={openDeleteStudentModal}
                className="delete-button"
              >
                - 학생 삭제
              </button>
            </div>
          </div>

          <div className="students-section">
            {/* 학생 검색 */}
            <div className="student-search-section">
              <label htmlFor="studentSearch" className="filter-label">
                학생 검색:
              </label>
              <input
                type="text"
                id="studentSearch"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                placeholder="이름, 영어 이름, 코드로 검색하세요..."
                className="student-search-input"
              />
            </div>

            {loading ? (
              <div className="loading">학생 목록을 불러오는 중...</div>
            ) : (
              <div className="students-table-container">
                {students.length > 0 ? (
                  <table className="students-table">
                    <thead>
                      <tr>
                        <th>이름</th>
                        <th>영어 이름</th>
                        <th>코드</th>
                        <th>반</th>
                        <th>작업</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students
                        .filter((student) => {
                          if (!studentSearchTerm) return true;
                          const searchLower = studentSearchTerm.toLowerCase();
                          return (
                            student.name.toLowerCase().includes(searchLower) ||
                            student.englishName.toLowerCase().includes(searchLower) ||
                            student.code.toLowerCase().includes(searchLower)
                          );
                        })
                        .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                        .map((student) => (
                          <tr key={student.code} className="student-row">
                            <td>{student.name}</td>
                            <td>{student.englishName}</td>
                            <td>{student.code}</td>
                            <td>{student.class || '미지정'}</td>
                            <td>
                              <button 
                                onClick={() => openEditStudentModal(student)}
                                className="edit-button"
                                title="학생 정보 수정"
                              >
                                수정
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-students">
                    <p>등록된 학생이 없습니다.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

            {/* 선생님 관리 탭 */}
            {activeTab === 'teachers' && (
              <div className="tab-content">
                <div className="tab-header">
                  <h3>선생님 관리</h3>
                  <div className="tab-actions">
                    <button 
                      onClick={() => setShowAddTeacherModal(true)}
                      className="add-button"
                    >
                      + 선생님 추가
                    </button>
                    <button 
                      onClick={handleCleanupTeacherData}
                      className="cleanup-button"
                      disabled={loading}
                    >
                      🔧 선생님 데이터 정리
                    </button>
                  </div>
                </div>

                <div className="teachers-section">
                  {loading ? (
                    <div className="loading">선생님 목록을 불러오는 중...</div>
                  ) : teachers.length > 0 ? (
                    <>
                      {/* 선생님 필터링 드롭다운 */}
                      <div className="teacher-filter-section">
                        <label htmlFor="teacherFilter" className="filter-label">
                          선생님 필터:
                        </label>
                        <select
                          id="teacherFilter"
                          value={selectedTeacherFilter}
                          onChange={(e) => setSelectedTeacherFilter(e.target.value)}
                          className="teacher-filter-dropdown"
                        >
                          <option value="all">전체 선생님 보기</option>
                          {teachers
                            .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                            .map((teacher) => (
                              <option key={teacher.code} value={teacher.code}>
                                {teacher.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div className="teachers-grouped-container">
                        {teachers
                          .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                          .filter((teacher) => 
                            selectedTeacherFilter === 'all' || teacher.code === selectedTeacherFilter
                          )
                          .map((teacher) => (
                            <TeacherGroup 
                              key={teacher.code}
                              teacher={teacher}
                              onEditTeacher={() => openEditTeacherModal(teacher)}
                              onDeleteTeacher={() => {
                                setItemToDelete(teacher);
                                setShowDeleteTeacherModal(true);
                              }}
                            />
                          ))}
                      </div>
                    </>
                  ) : (
                    <div className="no-teachers">
                      <p>등록된 선생님이 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

      {/* 반 관리 탭 */}
      {activeTab === 'classes' && (
        <div className="tab-content">
          <div className="tab-header">
            <h3>반 관리</h3>
            <div className="tab-actions">
              <button 
                onClick={() => setShowAddClassModal(true)}
                className="add-button"
              >
                + 반 추가
              </button>
            </div>
          </div>

          <div className="classes-section">
            {/* 필터링 섹션 */}
            <div className="class-filter-section" style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
              {/* 선생님 필터 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="teacherFilterForClasses" className="filter-label">
                  선생님 필터:
                </label>
                <select
                  id="teacherFilterForClasses"
                  value={selectedTeacherFilterForClasses}
                  onChange={(e) => {
                    setSelectedTeacherFilterForClasses(e.target.value);
                    setSelectedClassFilter('all'); // 선생님 필터 변경 시 반 필터 리셋
                  }}
                  className="class-filter-dropdown"
                >
                  <option value="all">전체</option>
                  {teachers
                    .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                    .map((teacher) => (
                      <option key={teacher.code} value={teacher.code}>
                        {teacher.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* 반 필터 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="classFilter" className="filter-label">
                  반 필터:
                </label>
                <select
                  id="classFilter"
                  value={selectedClassFilter}
                  onChange={(e) => setSelectedClassFilter(e.target.value)}
                  className="class-filter-dropdown"
                >
                  <option value="all">전체 반 보기</option>
                  {(selectedTeacherFilterForClasses === 'all' 
                    ? classes 
                    : classes.filter((classItem) => classItem.teacher === selectedTeacherFilterForClasses)
                  )
                    .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                    .map((classItem) => (
                      <option key={classItem.name} value={classItem.name}>
                        {classItem.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading">반 목록을 불러오는 중...</div>
            ) : (
              <div className="students-grouped-container">
                {classes.length > 0 ? (
                  classes
                    .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                    .filter((classItem) => {
                      // 선생님 필터 적용
                      const teacherMatch = selectedTeacherFilterForClasses === 'all' || 
                        classItem.teacher === selectedTeacherFilterForClasses;
                      // 반 필터 적용
                      const classMatch = selectedClassFilter === 'all' || 
                        classItem.name === selectedClassFilter;
                      return teacherMatch && classMatch;
                    })
                    .map((classItem) => (
                      <ClassGroup 
                        key={classItem.name}
                        classItem={classItem}
                        onEditClass={() => openEditClassModal(classItem)}
                        onDeleteClass={() => {
                          setItemToDelete(classItem);
                          setShowDeleteClassModal(true);
                        }}
                        onEditStudent={(student: any) => openEditStudentModal(student)}
                        onDeleteStudent={(student: any) => {
                          setItemToDelete(student);
                          setShowDeleteStudentModal(true);
                        }}
                      />
                    ))
                ) : (
                  <div className="no-classes">
                    <p>등록된 반이 없습니다. 먼저 반을 추가해주세요.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 반관리Test 탭 */}
      {activeTab === 'classesTest' && (
        <div className="tab-content">
          <div className="tab-header">
            <h3>반관리Test</h3>
          </div>

          <div className="classes-test-section">
            {/* 선생님 필터 */}
            <div className="teacher-filter-section" style={{ marginBottom: '1rem' }}>
              <label htmlFor="teacherFilterForTest" className="filter-label">
                선생님 필터:
              </label>
              <select
                id="teacherFilterForTest"
                value={selectedTeacherFilterForTest}
                onChange={(e) => setSelectedTeacherFilterForTest(e.target.value)}
                className="class-filter-dropdown"
              >
                <option value="all">전체</option>
                {teachers
                  .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                  .map((teacher) => (
                    <option key={teacher.code} value={teacher.code}>
                      {teacher.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* 시간표 캘린더 */}
            {selectedTeacherFilterForTest !== 'all' && (
              <div className="schedule-calendar">
                <table className="schedule-table">
                  <thead>
                    <tr>
                      <th className="time-header">시간</th>
                      <th>월</th>
                      <th>화</th>
                      <th>수</th>
                      <th>목</th>
                      <th>금</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 9 }, (_, i) => {
                      const hour = 9 + i;
                      const timeLabel = `${hour}:00 - ${hour + 1}:00`;
                      return (
                        <tr key={hour}>
                          <td className="time-cell">{timeLabel}</td>
                          <td className="schedule-cell"></td>
                          <td className="schedule-cell"></td>
                          <td className="schedule-cell"></td>
                          <td className="schedule-cell"></td>
                          <td className="schedule-cell"></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {selectedTeacherFilterForTest === 'all' && (
              <div className="no-selection-message">
                <p>선생님을 선택하면 시간표가 표시됩니다.</p>
              </div>
            )}
          </div>
        </div>
      )}


      {/* 학생 추가 모달 */}
      {showAddStudentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>새 학생 추가</h3>
              <button className="modal-close-button" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="student-form">
              <div className="form-group">
                <label htmlFor="code">학생 코드 *</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={studentForm.code}
                  onChange={handleInputChange}
                  placeholder="학생 코드를 입력하세요 (예: ABC12)"
                  className={formErrors.code ? 'error' : ''}
                />
                {formErrors.code && <span className="error-message">{formErrors.code}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="name">이름 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={studentForm.name}
                  onChange={handleInputChange}
                  placeholder="학생 이름을 입력하세요"
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="englishName">영어 이름 *</label>
                <input
                  type="text"
                  id="englishName"
                  name="englishName"
                  value={studentForm.englishName}
                  onChange={handleInputChange}
                  placeholder="영어 이름을 입력하세요"
                  className={formErrors.englishName ? 'error' : ''}
                />
                {formErrors.englishName && <span className="error-message">{formErrors.englishName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="class">반</label>
                <select
                  id="class"
                  name="class"
                  value={studentForm.class}
                  onChange={handleInputChange}
                >
                  <option value="">반 선택 안함 (not assigned 반에 소속)</option>
                  {classes.map((classItem) => (
                    <option key={classItem.name} value={classItem.name}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>



              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  className="cancel-button"
                  disabled={submitting}
                >
                  취소
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? '추가 중...' : '학생 추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 학생 수정 모달 */}
      {showEditStudentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>학생 정보 수정</h3>
              <button className="modal-close-button" onClick={handleCloseEditModal}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="student-form">
              <div className="form-group">
                <label htmlFor="editCode">학생 코드 *</label>
                <input
                  type="text"
                  id="editCode"
                  name="code"
                  value={editStudentForm.code}
                  onChange={handleEditInputChange}
                  placeholder="학생 코드를 입력하세요 (예: ABC12)"
                  className={editFormErrors.code ? 'error' : ''}
                />
                {editFormErrors.code && <span className="error-message">{editFormErrors.code}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="editName">이름 *</label>
                <input
                  type="text"
                  id="editName"
                  name="name"
                  value={editStudentForm.name}
                  onChange={handleEditInputChange}
                  placeholder="학생 이름을 입력하세요"
                  className={editFormErrors.name ? 'error' : ''}
                />
                {editFormErrors.name && <span className="error-message">{editFormErrors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="editEnglishName">영어 이름 *</label>
                <input
                  type="text"
                  id="editEnglishName"
                  name="englishName"
                  value={editStudentForm.englishName}
                  onChange={handleEditInputChange}
                  placeholder="영어 이름을 입력하세요"
                  className={editFormErrors.name ? 'error' : ''}
                />
                {editFormErrors.englishName && <span className="error-message">{editFormErrors.englishName}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="editClass">반</label>
                <select
                  id="editClass"
                  name="class"
                  value={editStudentForm.class}
                  onChange={handleEditInputChange}
                >
                  <option value="">반 선택 안함 (not assigned 반에 소속)</option>
                  {classes.map((classItem) => (
                    <option key={classItem.name} value={classItem.name}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>



              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseEditModal}
                  className="cancel-button"
                  disabled={editing}
                >
                  취소
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={editing}
                >
                  {editing ? '수정 중...' : '수정 완료'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

                     {/* 선생님 추가 모달 */}
        {showAddTeacherModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>새 선생님 추가</h3>
                <button className="modal-close-button" onClick={handleCloseTeacherModal}>
                  ×
                </button>
              </div>
              
              <form onSubmit={handleTeacherSubmit} className="teacher-form">
                <div className="form-group">
                  <label htmlFor="teacherCode">선생님 코드 *</label>
                  <input
                    type="text"
                    id="teacherCode"
                    name="code"
                    value={teacherForm.code}
                    onChange={handleTeacherInputChange}
                    placeholder="선생님 코드를 입력하세요 (예: TCH01)"
                    className={teacherFormErrors.code ? 'error' : ''}
                  />
                  {teacherFormErrors.code && <span className="error-message">{teacherFormErrors.code}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="teacherName">이름 *</label>
                  <input
                    type="text"
                    id="teacherName"
                    name="name"
                    value={teacherForm.name}
                    onChange={handleTeacherInputChange}
                    placeholder="선생님 이름을 입력하세요"
                    className={teacherFormErrors.name ? 'error' : ''}
                  />
                  {teacherFormErrors.name && <span className="error-message">{teacherFormErrors.name}</span>}
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleCloseTeacherModal}
                    className="cancel-button"
                    disabled={teacherSubmitting}
                  >
                    취소
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={teacherSubmitting}
                  >
                    {teacherSubmitting ? '추가 중...' : '선생님 추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 선생님 수정 모달 */}
        {showEditTeacherModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>선생님 정보 수정</h3>
                <button className="modal-close-button" onClick={handleCloseEditTeacherModal}>
                  ×
                </button>
              </div>
              
              <form onSubmit={handleEditTeacherSubmit} className="teacher-form">
                <div className="form-group">
                  <label htmlFor="editTeacherCode">선생님 코드 *</label>
                  <input
                    type="text"
                    id="editTeacherCode"
                    name="code"
                    value={teacherForm.code}
                    onChange={handleTeacherInputChange}
                    placeholder="선생님 코드를 입력하세요 (예: TCH01)"
                    className={teacherFormErrors.code ? 'error' : ''}
                  />
                  {teacherFormErrors.code && <span className="error-message">{teacherFormErrors.code}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="editTeacherName">이름 *</label>
                  <input
                    type="text"
                    id="editTeacherName"
                    name="name"
                    value={teacherForm.name}
                    onChange={handleTeacherInputChange}
                    placeholder="선생님 이름을 입력하세요"
                    className={teacherFormErrors.name ? 'error' : ''}
                  />
                  {teacherFormErrors.name && <span className="error-message">{teacherFormErrors.name}</span>}
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleCloseEditTeacherModal}
                    className="cancel-button"
                    disabled={teacherSubmitting}
                  >
                    취소
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={teacherSubmitting}
                  >
                    {teacherSubmitting ? '수정 중...' : '수정 완료'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

               {/* 반 추가 모달 */}
        {showAddClassModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>새 반 추가</h3>
                <button className="modal-close-button" onClick={handleCloseClassModal}>
                  ×
                </button>
              </div>
              
              <form onSubmit={handleClassSubmit} className="class-form">
                                 <div className="form-group">
                   <label htmlFor="className">반 이름 *</label>
                   <input
                     type="text"
                     id="className"
                     name="name"
                     value={classForm.name}
                     onChange={handleClassInputChange}
                     placeholder="반 이름을 입력하세요 (예: 1학년1반 또는 1-1)"
                     className={classFormErrors.name ? 'error' : ''}
                   />
                   <small className="form-hint">⚠️ 점(.), #, $, [, ], / 문자는 사용할 수 없습니다. 대신 하이픈(-)이나 공백을 사용하세요.</small>
                   {classFormErrors.name && <span className="error-message">{classFormErrors.name}</span>}
                 </div>

                                 <div className="form-group">
                   <label htmlFor="classTeacher">담당 선생님</label>
                   <select
                     id="classTeacher"
                     name="teacher"
                     value={classForm.teacher}
                     onChange={handleClassInputChange}
                   >
                     <option value="">담당 선생님 선택 (선택사항)</option>
                     {teachers.map((teacher) => (
                       <option key={teacher.code} value={teacher.code}>
                         {teacher.name} ({teacher.code})
                       </option>
                     ))}
                   </select>
                 </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleCloseClassModal}
                    className="cancel-button"
                    disabled={classSubmitting}
                  >
                    취소
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={classSubmitting}
                  >
                    {classSubmitting ? '추가 중...' : '반 추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 반 수정 모달 */}
        {showEditClassModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>반 정보 수정</h3>
                <button className="modal-close-button" onClick={handleCloseEditClassModal}>
                  ×
                </button>
              </div>
              
              <form onSubmit={handleEditClassSubmit} className="class-form">
                                 <div className="form-group">
                   <label htmlFor="editClassName">반 이름 *</label>
                   <input
                     type="text"
                     id="editClassName"
                     name="name"
                     value={classForm.name}
                     onChange={handleClassInputChange}
                     placeholder="반 이름을 입력하세요 (예: 1학년1반 또는 1-1)"
                     className={classFormErrors.name ? 'error' : ''}
                   />
                   <small className="form-hint">⚠️ 점(.), #, $, [, ], / 문자는 사용할 수 없습니다. 대신 하이픈(-)이나 공백을 사용하세요.</small>
                   {classFormErrors.name && <span className="error-message">{classFormErrors.name}</span>}
                 </div>

                                 <div className="form-group">
                   <label htmlFor="editClassTeacher">담당 선생님</label>
                   <select
                     id="editClassTeacher"
                     name="teacher"
                     value={classForm.teacher}
                     onChange={handleClassInputChange}
                   >
                     <option value="">담당 선생님 선택 (선택사항)</option>
                     {teachers.map((teacher) => (
                       <option key={teacher.code} value={teacher.code}>
                         {teacher.name} ({teacher.code})
                       </option>
                     ))}
                   </select>
                 </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleCloseEditClassModal}
                    className="cancel-button"
                    disabled={classSubmitting}
                  >
                    취소
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={classSubmitting}
                  >
                    {classSubmitting ? '수정 중...' : '수정 완료'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* 학생 삭제 모달 */}
      {showDeleteStudentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>학생 삭제</h3>
              <button className="modal-close-button" onClick={closeDeleteModal}>
                ×
              </button>
            </div>
            
            <div className="delete-modal-content">
              {!itemToDelete ? (
                <form onSubmit={searchStudentForDeletion} className="delete-form">
                  <div className="form-group">
                    <label htmlFor="deleteCode">삭제할 학생의 코드를 입력하세요</label>
                    <input
                      type="text"
                      id="deleteCode"
                      name="code"
                      value={deleteForm.code}
                      onChange={handleDeleteInputChange}
                      placeholder="학생 코드를 입력하세요"
                      className={deleteFormErrors.code ? 'error' : ''}
                    />
                    {deleteFormErrors.code && <span className="error-message">{deleteFormErrors.code}</span>}
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      onClick={closeDeleteModal}
                      className="cancel-button"
                    >
                      취소
                    </button>
                    <button 
                      type="submit" 
                      className="submit-button"
                    >
                      학생 찾기
                    </button>
                  </div>
                </form>
              ) : (
                <div className="delete-confirmation">
                  <div className="delete-warning">
                    <h4>⚠️ 삭제 확인</h4>
                    <p>다음 학생을 삭제하시겠습니까?</p>
                  </div>
                  
                  <div className="item-info">
                    <h5>{itemToDelete.name} ({itemToDelete.englishName})</h5>
                                         <p><strong>코드:</strong> {itemToDelete.code}</p>
                     {itemToDelete.class && <p><strong>반:</strong> {itemToDelete.class}</p>}
                  </div>
                  
                  <div className="delete-note">
                    <p><strong>주의:</strong> 이 작업은 되돌릴 수 없습니다.</p>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      onClick={closeDeleteModal}
                      className="cancel-button"
                      disabled={deleting}
                    >
                      취소
                    </button>
                    <button 
                      type="button" 
                      onClick={handleDeleteStudent}
                      className="delete-confirm-button"
                      disabled={deleting}
                    >
                      {deleting ? '삭제 중...' : '삭제 확인'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

             {/* 선생님 삭제 모달 */}
       {showDeleteTeacherModal && (
         <div className="modal-overlay">
           <div className="modal-content">
             <div className="modal-header">
               <h3>선생님 삭제</h3>
               <button className="modal-close-button" onClick={closeDeleteModal}>
                 ×
               </button>
             </div>
             
             <div className="delete-modal-content">
               {!itemToDelete ? (
                 <form onSubmit={searchTeacherForDeletion} className="delete-form">
                   <div className="form-group">
                     <label htmlFor="deleteTeacherCode">삭제할 선생님의 코드를 입력하세요</label>
                     <input
                       type="text"
                       id="deleteTeacherCode"
                       name="code"
                       value={deleteForm.code}
                       onChange={handleDeleteInputChange}
                       placeholder="선생님 코드를 입력하세요"
                       className={deleteFormErrors.code ? 'error' : ''}
                     />
                     {deleteFormErrors.code && <span className="error-message">{deleteFormErrors.code}</span>}
                   </div>

                   <div className="form-actions">
                     <button 
                       type="button" 
                       onClick={closeDeleteModal}
                       className="cancel-button"
                     >
                       취소
                     </button>
                     <button 
                       type="submit" 
                       className="submit-button"
                     >
                       선생님 찾기
                     </button>
                   </div>
                 </form>
               ) : (
                 <div className="delete-confirmation">
                   <div className="delete-warning">
                     <h4>⚠️ 삭제 확인</h4>
                     <p>다음 선생님을 삭제하시겠습니까?</p>
                   </div>
                   
                   <div className="item-info">
                     <h5>{itemToDelete.name}</h5>
                     <p><strong>코드:</strong> {itemToDelete.code}</p>
                   </div>
                   
                   <div className="delete-note">
                     <p><strong>주의:</strong> 이 작업은 되돌릴 수 없습니다.</p>
                   </div>

                   <div className="form-actions">
                     <button 
                       type="button" 
                       onClick={closeDeleteModal}
                       className="cancel-button"
                       disabled={deleting}
                     >
                       취소
                     </button>
                     <button 
                       type="button" 
                       onClick={handleDeleteTeacher}
                       className="delete-confirm-button"
                       disabled={deleting}
                     >
                       {deleting ? '삭제 중...' : '삭제 확인'}
                     </button>
                   </div>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}

       {/* 반 삭제 모달 */}
       {showDeleteClassModal && (
         <div className="modal-overlay">
           <div className="modal-content">
             <div className="modal-header">
               <h3>반 삭제</h3>
               <button className="modal-close-button" onClick={closeDeleteModal}>
                 ×
               </button>
             </div>
             
             <div className="delete-modal-content">
               {!itemToDelete ? (
                 <form onSubmit={searchClassForDeletion} className="delete-form">
                   <div className="form-group">
                     <label htmlFor="deleteClassName">삭제할 반의 이름을 입력하세요</label>
                     <input
                       type="text"
                       id="deleteClassName"
                       name="code"
                       value={deleteForm.code}
                       onChange={handleDeleteInputChange}
                       placeholder="반 이름을 입력하세요"
                       className={deleteFormErrors.code ? 'error' : ''}
                     />
                     {deleteFormErrors.code && <span className="error-message">{deleteFormErrors.code}</span>}
                   </div>

                   <div className="form-actions">
                     <button 
                       type="button" 
                       onClick={closeDeleteModal}
                       className="cancel-button"
                     >
                       취소
                     </button>
                     <button 
                       type="submit" 
                       className="submit-button"
                     >
                       반 찾기
                     </button>
                   </div>
                 </form>
               ) : (
                 <div className="delete-confirmation">
                   <div className="delete-warning">
                     <h4>⚠️ 삭제 확인</h4>
                     <p>다음 반을 삭제하시겠습니까?</p>
                   </div>
                   
                                        <div className="item-info">
                       <h5>{itemToDelete.name}</h5>
                       {itemToDelete.teacherName && <p><strong>담당 선생님:</strong> {itemToDelete.teacherName}</p>}
                     </div>
                   
                   <div className="delete-note">
                     <p><strong>주의:</strong> 이 작업은 되돌릴 수 없습니다.</p>
                   </div>

                   <div className="form-actions">
                     <button 
                       type="button" 
                       onClick={closeDeleteModal}
                       className="cancel-button"
                       disabled={deleting}
                     >
                       취소
                     </button>
                     <button 
                       type="button" 
                       onClick={handleDeleteClass}
                       className="delete-confirm-button"
                       disabled={deleting}
                     >
                       {deleting ? '삭제 중...' : '삭제 확인'}
                     </button>
                   </div>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}

               {/* 학생 목록 모달 */}
        {showStudentListModal && (
          <div className="modal-overlay">
            <div className="modal-content student-list-modal">
              <div className="modal-header">
                <h3>{selectedClassName} 반 학생 목록</h3>
                <button className="modal-close-button" onClick={handleCloseStudentListModal}>
                  ×
                </button>
              </div>
              
              <div className="student-list-content">
                {selectedClassStudents.length > 0 ? (
                  <div className="students-table-container">
                    <table className="students-table">
                                            <thead>
                         <tr>
                           <th>이름</th>
                           <th>영어 이름</th>
                           <th>코드</th>
                         </tr>
                       </thead>
                      <tbody>
                        {selectedClassStudents
                          .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                          .map((student) => (
                                                    <tr key={student.id} className="student-row">
                             <td>{student.name}</td>
                             <td>{student.englishName || '-'}</td>
                             <td>{student.code}</td>
                           </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-students">
                    <p>이 반에 소속된 학생이 없습니다.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 선생님 담당반 모달 */}
        {showTeacherClassesModal && (
          <div className="modal-overlay">
            <div className="modal-content student-list-modal">
              <div className="modal-header">
                <h3>{selectedTeacherName} 선생님 담당반 목록</h3>
                <button className="modal-close-button" onClick={handleCloseTeacherClassesModal}>
                  ×
                </button>
              </div>
              
              <div className="student-list-content">
                {selectedTeacherClasses.length > 0 ? (
                  <div className="students-table-container">
                    <table className="students-table">
                      <thead>
                        <tr>
                          <th>반 이름</th>
                          <th>추가일</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTeacherClasses
                          .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                          .map((classItem) => (
                            <tr key={classItem.id} className="student-row">
                              <td>{classItem.name}</td>
                              <td>{new Date(classItem.addedAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-students">
                    <p>이 선생님이 담당하는 반이 없습니다.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* 선생님 학생 관리 모달 */}
      {showTeacherManageModal && selectedTeacher && (
        <div className="modal-overlay">
          <div className="modal-content teacher-manage-modal">
            <div className="modal-header">
              <h3>{selectedTeacher.name} 선생님 학생 관리</h3>
              <button className="modal-close-button" onClick={closeTeacherManageModal}>
                ×
              </button>
            </div>
            
            <div className="teacher-manage-content">
              {/* 학생 추가 폼 */}
              <div className="add-student-section">
                <h4>학생 추가</h4>
                <form onSubmit={addStudentToTeacher} className="manage-student-form">
                  <div className="form-group">
                    <label htmlFor="manageStudentCode">추가할 학생의 코드를 입력하세요</label>
                    <input
                      type="text"
                      id="manageStudentCode"
                      name="code"
                      value={manageStudentForm.code}
                      onChange={handleManageStudentInputChange}
                      placeholder="학생 코드를 입력하세요"
                      className={manageStudentFormErrors.code ? 'error' : ''}
                    />
                    {manageStudentFormErrors.code && <span className="error-message">{manageStudentFormErrors.code}</span>}
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="submit-button"
                      disabled={managingStudent}
                    >
                      {managingStudent ? '추가 중...' : '학생 추가'}
                    </button>
                  </div>
                </form>
              </div>

              {/* 관리 중인 학생 목록 */}
              <div className="managed-students-section">
                <h4>관리 중인 학생 목록</h4>
                {managedStudents.length > 0 ? (
                  <div className="managed-students-list">
                    {managedStudents.map((student) => (
                      <div key={student.code} className="managed-student-item">
                        <div className="managed-student-info">
                          <h5>{student.name} ({student.englishName})</h5>
                          {student.class && <p><strong>반:</strong> {student.class}</p>}
                          <p><strong>코드:</strong> {student.code}</p>
                          <p><strong>추가일:</strong> {new Date(student.addedAt).toLocaleDateString()}</p>
                        </div>
                        <button 
                          onClick={() => removeStudentFromTeacher(student.code)}
                          className="remove-student-button"
                        >
                          제거
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-managed-students">
                    <p>아직 관리하는 학생이 없습니다.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
